import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { CalendarEvent } from 'angular-calendar';
import { Observable, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import {
  EConsultaTipo,
  EDuration,
  ESolicitacaoConsultaModalidade,
  ESolicitacaoConsultaStatus,
  IConsulta,
  ISolicitacaoConsultaFiltros,
  ISolicitaoConsulta
} from 'src/app/pages/consultas/data/consultas';
import { ConsultasService } from 'src/app/pages/consultas/services/consultas.service';
import { IListByFilterMedico, IMedico } from 'src/app/pages/doctors/models/doctor';
import { DoctorService } from 'src/app/pages/doctors/services/doctor.service';
import { EOrdenacao } from 'src/app/shared/data/ordenacao.enum';
import { EMessageType } from 'src/app/shared/data/response-default.interface';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { SessionService } from 'src/app/shared/services/session.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { IFiltrosAgenda } from '../../models/agenda';
import { AgendaService } from '../../services/agenda.service';

@Component({
  selector: 'app-marcar-consulta-modal',
  templateUrl: './marcar-consulta-modal.component.html',
  styleUrls: ['./marcar-consulta-modal.component.scss']
})
export class MarcarConsultaModalComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subject<any>();
  isDoctor = true;

  ERoleAdminString = ERoleAdminString;

  @Input() appointmentDate!: Date;
  @Output() closeModal = new EventEmitter();

  scheduleDate!: Date;
  doctors: IMedico[] = [];
  durations = [
    {
      value: EDuration.QuinzeMin,
      label: '15 Min'
    },
    {
      value: EDuration.TrintaMIn,
      label: '30 Min'
    }
  ];

  events: CalendarEvent[] = [];
  locale: string = 'pt-BR';

  form = new UntypedFormGroup({
    employeeId: new UntypedFormControl(''),
    duration: new UntypedFormControl(EDuration.QuinzeMin, [Validators.required]),
    doctorId: new UntypedFormControl('', [Validators.required]),
    modality: new UntypedFormControl(ESolicitacaoConsultaModalidade.Telemedicina),
    type: new UntypedFormControl(EConsultaTipo.Rotina),
    medicalAppointmentRequestId: new UntypedFormControl('', Validators.required)
  });

  dadosPaginacao = {
    ordenacao: EOrdenacao.MaisNovos,
    paginaSelecionada: 0,
    tamanhoPagina: 6,
    totalPaginas: 0,
    itensTotal: 0,
    periodStart: 0,
    periodEnd: 0
  };

  consultas: ISolicitaoConsulta[] = [];

  constructor(
    private readonly agendaService: AgendaService,
    private readonly consultasService: ConsultasService,
    private readonly doctorService: DoctorService,
    private readonly toastService: ToastService,
    private readonly sessionService: SessionService
  ) { }

  ngOnInit(): void {
    this.isDoctor = this.sessionService.getSession().role === ERoleAdminString.Doctor;

    this.scheduleDate = new Date(this.appointmentDate);
    this.carregarMedicos();
    this.carregarConsultas();
    this.getEventosAgendaMedico()
      .pipe(take(1))
      .subscribe((consultasAgenda) => (this.events = consultasAgenda));

    this.durationControl.valueChanges.pipe(takeUntil(this.subscriptions)).subscribe((duration) => {
      this.editScheduleEvent(+duration);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.next(null);
    this.subscriptions.complete();
  }

  agendarConsulta() {
    if (!this.validateForm()) {
      return;
    }

    const dadosForm = this.form.getRawValue();
    const appointmentDate = new Date(this.scheduleDate.getTime() - this.scheduleDate.getTimezoneOffset() * 60000);
    const dados: IConsulta = {
      ...dadosForm,
      appointmentDate: appointmentDate
    };

    this.consultasService.criarConsulta(dados).subscribe((res) => {
      if (res?.messages) {
        this.toastService.toast(res?.messages);
      }

      if (res.isSucceed) {
        this.hideModalConsulta();
      }
    });
  }

  selectTime(event: { date: Date }): void {
    if (this.scheduleDate) {
      this.events.pop();
    }

    this.scheduleDate = event.date;
    const startDate = new Date(event.date);

    this.events = [...this.events, this.createScheduleEvent(startDate)];
  }

  selectRequestId(medicalAppointmentRequestId: string, employeeId: string): void {
    this.employeeIdControl.patchValue(employeeId);
    this.medicalAppointmentRequestIdControl.patchValue(medicalAppointmentRequestId);
  }

  hideModalConsulta(): void {
    this.closeModal.emit();
  }

  carregarConsultas(): void {
    const filtros: ISolicitacaoConsultaFiltros = {
      orderBy: Number(this.dadosPaginacao.ordenacao),
      pageNumber: Number(this.dadosPaginacao.paginaSelecionada),
      pageSize: Number(this.dadosPaginacao.tamanhoPagina),
      searchText: this.agendaService.getNomeDoDiaByData(this.appointmentDate),
      periodStart: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
      periodEnd: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString(),
      status: ESolicitacaoConsultaStatus.pendenteAprovacao
    };

    const partnerId = this.sessionService.getSession().partnerId;
    const companyId = this.sessionService.getSession().companyId;
    const accountRole = this.sessionService.getSession().accountRole;

    if (partnerId) {
      filtros.partnerId = partnerId;
    }

    if (companyId) {
      filtros.companyId = companyId;
    }

    if (accountRole) {
      filtros.accountRole = parseInt(accountRole);
    }

    this.consultasService.listarTodasSolicitacoesConsultas(filtros).subscribe((res) => {
      this.consultas = res.data;
      this.dadosPaginacao.totalPaginas = Math.ceil(res.count! / this.dadosPaginacao.tamanhoPagina);
      this.dadosPaginacao.itensTotal = res.count!;
    });
  }

  mudouPagina(paginaSelecionada: number): void {
    this.dadosPaginacao.paginaSelecionada = paginaSelecionada;

    this.carregarConsultas();
  }

  mudouTamanhoPagina(itensPaginaSelecionada: number): void {
    this.dadosPaginacao.tamanhoPagina = itensPaginaSelecionada;
    this.dadosPaginacao.paginaSelecionada = 0;

    this.carregarConsultas();
  }

  get employeeIdValue() {
    return this.employeeIdControl.value;
  }

  get medicalAppointmentRequestIdValue() {
    return this.medicalAppointmentRequestIdControl.value;
  }

  private carregarMedicos(): void {
    if (!this.isDoctor) {
      const partnerId = this.sessionService.getSession().partnerId;
      const accountRole = this.sessionService.getSession().accountRole;
      const companyId = this.sessionService.getSession().companyId;

      const filtros: IListByFilterMedico = {
        pageNumber: 0,
        pageSize: 9999,
        orderBy: EOrdenacao.AlfabeticaAZ
      };

      if (accountRole) {
        filtros.accountRole = parseInt(accountRole);
      }

      if (partnerId) {
        filtros.partnerId = partnerId;
      }

      if (companyId) {
        filtros.companyId = companyId;
      }

      this.doctorService
        .listarTodosMedicos(filtros)
        .pipe(take(1))
        .subscribe((res) => {
          if (res.isSucceed) {
            this.doctors = res.data;
            this.doctorControl.patchValue(res.data[0]?.id);
          }
        });
    } else {
      this.doctorControl.patchValue(this.sessionService.getSession().sid, { emitEvent: false });
    }

    this.doctorControl.valueChanges
      .pipe(switchMap(() => this.getEventosAgendaMedico()))
      .subscribe((events) => (this.events = events));
  }

  private getEventosAgendaMedico(): Observable<CalendarEvent[]> {
    const filtros = this.getFiltrosAgendaMedico(this.appointmentDate);

    return this.agendaService.carregarAgendaMedico(filtros).pipe(
      takeUntil(this.subscriptions),
      map((res) => {
        const events: CalendarEvent[] = res.data.map((consulta: IConsulta) => {
          const horaComecoConsulta = new Date(consulta.appointmentDate);

          return this.agendaService.createScheduleEvent(
            horaComecoConsulta,
            consulta.duration,
            consulta.employee?.name!,
            consulta.status!,
            consulta.modality,
            consulta.id
          );
        });

        if (this.scheduleDate && this.appointmentDate) {
          events.push(
            this.createScheduleEvent(
              new Date(
                this.appointmentDate.getFullYear(),
                this.appointmentDate.getMonth(),
                this.appointmentDate.getDate(),
                this.scheduleDate.getHours(),
                this.scheduleDate.getMinutes()
              )
            )
          );
        }

        return events;
      })
    );
  }

  private getFiltrosAgendaMedico(data: Date): IFiltrosAgenda {
    const partnerId = this.sessionService.getSession().partnerId;
    const accountRole = this.sessionService.getSession().accountRole;
    const companyId = this.sessionService.getSession().companyId;

    const filtros: IFiltrosAgenda = {
      doctorId: this.doctorControl.value,
      periodStart: new Date(data.getFullYear(), data.getMonth(), data.getDate()).toISOString(),
      periodEnd: new Date(data.getFullYear(), data.getMonth(), data.getDate() + 1).toISOString()
    };

    if (accountRole) {
      filtros.accountRole = parseInt(accountRole);
    }

    if (partnerId) {
      filtros.partnerId = partnerId;
    }

    if (companyId) {
      filtros.companyId = companyId;
    }

    return filtros;
  }

  private get doctorControl(): UntypedFormControl {
    return this.form.get('doctorId') as UntypedFormControl;
  }

  private get durationControl(): UntypedFormControl {
    return this.form.get('duration') as UntypedFormControl;
  }

  private get employeeIdControl(): UntypedFormControl {
    return this.form.get('employeeId') as UntypedFormControl;
  }

  private get medicalAppointmentRequestIdControl(): UntypedFormControl {
    return this.form.get('medicalAppointmentRequestId') as UntypedFormControl;
  }

  private get modalityControl(): UntypedFormControl {
    return this.form.get('modality') as UntypedFormControl;
  }

  private editScheduleEvent(duration: number): void {
    if (this.scheduleDate) {
      this.events = [
        ...this.events.map((event, index) => {
          if (index === this.events.length - 1) {
            event.end = this.agendaService.getEndScheduleDate(event.start, +duration);
          }

          return event;
        })
      ];
    }
  }

  private createScheduleEvent(date: Date) {
    return this.agendaService.createScheduleEvent(
      date,
      +(this.durationControl.value ?? 15),
      'NOVA CONSULTA',
      ESolicitacaoConsultaStatus.pendenteAprovacao,
      this.modalityControl.value
    );
  }

  private validateForm() {
    const solicitacaoConsulta = this.consultas.find(
      (consulta) => consulta.id === this.medicalAppointmentRequestIdValue
    );

    if (!this.scheduleDate) {
      this.toastService.toast('É necessário selecionar um horário.', EMessageType.Warning);
      return false;
    }

    if (!solicitacaoConsulta) {
      this.toastService.toast('Por favor selecione um colaborador.', EMessageType.Warning);
      return false;
    }

    if (this.form.invalid) {
      this.toastService.toast('Todos os campos são obrigatórios e devem ser preenchidos.', EMessageType.Warning);
      return false;
    }

    const horario = this.scheduleDate.getHours() + ':' + this.scheduleDate.getMinutes();
    if (!this.agendaService.checkHorarioValido(horario, solicitacaoConsulta.preferredTimes)) {
      this.toastService.toast('A hora selecionada não condiz com o que o colaborador solicitou.', EMessageType.Warning);
      return false;
    }

    return true;
  }
}
